import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  GUEST_FIELD_CATALOG,
  MAX_BOOKING_FILE_BYTES,
  normalizePhone,
  durationLabel,
  extraTermCost,
  type BookingAnswer,
  type BookingDay,
  type BookingResult,
  type PublicBookingPage,
} from "@goyone/shared";
import { api, qs } from "../../lib/api";
import { longDay, time } from "../../lib/format";
import { Icon } from "../../components/Icon";
import { Logo } from "../../components/Logo";
import { CountrySelect } from "../../components/CountrySelect";

/**
 * صفحة الحجز العامة — واجهة الشركة أمام عميلها.
 *
 * يفتحها عميلٌ لا حساب له فيختار وقتًا شاغرًا. تحلّ محلّ ثلاث رسائل ذهابًا
 * وإيابًا: «متى يناسبك؟» «الثلاثاء» «الثلاثاء مشغول، الأربعاء؟».
 *
 * ## لوحان: الهوية والاختيار
 *
 * لوح الهوية يحمل شعار الشركة واسمها وصاحب الوقت — ما يطمئن الغريب أنه في
 * المكان الصحيح — ولوح الاختيار يحمل الأوقات والنموذج معًا: اختيار الوقت
 * يبقى ظاهرًا حتى يُتمّ الحجز، فمن غيّر رأيه ينقر وقتًا آخر ولا يعود
 * خطوةً إلى الوراء.
 *
 * ## الشاشة والمصدر منفصلان
 *
 * `PublicBooking` يجلب الصفحة وأوقاتها من الخادم ثم يُسلّمها إلى
 * `BookingScreen` الذي يرسمها. والفصلُ ليس ترفًا: محرّرُ الصفحة يعرض
 * معاينةً حيّةً لصفحةٍ لم تُحفظ بعد — بلا رابطٍ يُجلب منه — فيُسلّم
 * الشاشةَ نفسَها بياناتِه هو، فيرى صاحبُها ما سيراه العميل حرفًا حرفًا.
 *
 * ## بلا هوية ولا ترويسة مستأجر
 *
 * `skipAuth` يمنع إرسال ترويسة شركةٍ محفوظة من زيارة سابقة أو رمزَ جلسةٍ
 * قائمة: من يفتح رابط شركةٍ وهو مسجَّل في أخرى يجب ألّا يُفسَّر طلبه في
 * نطاق شركته هو. والخادم يستنتج الشركة من الرابط لا من الترويسة.
 *
 * ## والأوقات تأتي محسوبة
 *
 * لا تُبنى الشبكة هنا من ساعات العمل: الخادم يعرف المحجوز والمهلة والسعة،
 * وحسابُها في المتصفح يعني شبكتين تختلفان — فيختار الزائر وقتًا يرفضه
 * الخادم بعد أن ملأ بياناته.
 */

const timeFmt = new Intl.DateTimeFormat("ar-EG-u-nu-latn", {
  hour: "2-digit",
  minute: "2-digit",
});

/**
 * رابط تشغيلٍ داخل إطار.
 *
 * رابط يوتيوب الذي يُنسخ من المتصفّح صفحةٌ كاملة لا مشغّل، ووضعُه في
 * إطارٍ يرفضه الموقع. فيُحوَّل إلى مسار التضمين، وما لا يُعرف يُترك
 * كما هو — رابطٌ لا يعمل خيرٌ من رابطٍ حُرِّف إلى غير موضعه.
 */
function embedUrl(url: string): string {
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return url;
}

/**
 * لونُ الكتابة على لون الشركة.
 *
 * الأزرارُ والوقتُ المختار تُصبغ بلون الشركة، وكتابتُها كانت بيضاء
 * دائمًا — فشركةٌ لونُها أصفر أو فاتح تُخفي كتابتها. يُحسب سطوعُ اللون
 * فتُكتب عليه بالأبيض إن كان داكنًا وبالداكن إن كان فاتحًا.
 */
function readableOn(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return "#fff";
  const n = parseInt(m[1]!, 16);
  const lin = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const L =
    0.2126 * lin((n >> 16) & 255) +
    0.7152 * lin((n >> 8) & 255) +
    0.0722 * lin(n & 255);
  return L > 0.45 ? "#12214a" : "#ffffff";
}

/**
 * الفصول تظهر حين تصل إليها العين.
 *
 * صفحةُ العرض طويلة، وظهورُ كلِّ فصلٍ عند بلوغه — انزلاقًا خفيفًا لا
 * قفزًا — هو ما يجعلها تُقرأ فصلًا فصلًا. يُراقَب كلُّ `.sh-reveal` مرّةً
 * ثم يُترك: ما ظهر لا يختفي إن عاد المرءُ فوقه.
 */
function useReveal(enabled: boolean, count: number) {
  useEffect(() => {
    if (!enabled) return;
    const els = Array.from(document.querySelectorAll(".sh-reveal"));
    /* ما هو في مرمى العين عند التحميل يظهر فورًا — لا ينتظر أول تمريرة. */
    const vh = window.innerHeight || 800;
    for (const el of els) {
      const r = el.getBoundingClientRect();
      if (r.top < vh * 0.9 && r.bottom > 0) el.classList.add("is-in");
    }
    if (!("IntersectionObserver" in window)) {
      els.forEach((el) => el.classList.add("is-in"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            e.target.classList.add("is-in");
            io.unobserve(e.target);
          }
        }
      },
      { threshold: 0.12 },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [enabled, count]);
}

/** كم يومًا تُطلب أوقاتها دفعةً — أسبوعان يكفيان لاختيار موعد. */
const WINDOW_DAYS = 14;

/** ما يُرسَل عند الحجز — الأربعة الثابتة وإجاباتُ ما زاد عليها. */
export interface BookingSubmission {
  startAt: string;
  name: string;
  email: string;
  phone?: string;
  note?: string;
  purpose?: string;
  mode?: string;
  durationMin?: number;
  answers?: Record<string, BookingAnswer>;
  agreedTerms?: boolean;
}

/* ==========================================================================
   المسار العامّ — يجلب ثم يُسلّم
   ========================================================================== */

export function PublicBooking() {
  const { tenant = "", slug = "" } = useParams();
  const base = `/public/booking/${encodeURIComponent(tenant)}/${encodeURIComponent(slug)}`;

  const page = useQuery({
    queryKey: ["booking", "page", tenant, slug],
    queryFn: () => api<PublicBookingPage>(base, { skipAuth: true }),
    retry: false,
  });

  const range = useMemo(() => {
    const from = new Date();
    const to = new Date(from);
    to.setDate(to.getDate() + WINDOW_DAYS);
    return { from: from.toISOString(), to: to.toISOString() };
  }, []);

  const slots = useQuery({
    queryKey: ["booking", "slots", tenant, slug],
    queryFn: () =>
      api<{ days: BookingDay[] }>(`${base}/slots${qs(range)}`, {
        skipAuth: true,
      }),
    enabled: page.isSuccess,
    retry: false,
  });

  /* ---------- الصفحة غير موجودة ---------- */
  if (page.isError) {
    return (
      <Frame>
        <div className="book-empty">
          <Icon name="calendar" size={32} />
          <h1 className="book-title">صفحة الحجز غير متاحة</h1>
          <p className="mute">
            الرابط غير صحيح أو أُوقفت الصفحة. راجع من أرسله إليك.
          </p>
        </div>
      </Frame>
    );
  }

  if (page.isPending) {
    return (
      <Frame>
        <div className="skeleton" style={{ height: 220 }} />
      </Frame>
    );
  }

  return (
    <BookingScreen
      info={page.data}
      days={slots.data?.days ?? []}
      slotsPending={slots.isPending}
      submit={(body) =>
        api<BookingResult>(base, { method: "POST", skipAuth: true, body })
      }
    />
  );
}

/* ==========================================================================
   المعاينة — الشاشة نفسها ببيانات المحرّر
   ========================================================================== */

/** ما يبعثه المحرّر إلى إطار المعاينة. */
export interface BookingPreviewMessage {
  type: "goyone.bookingPreview";
  info: PublicBookingPage;
  days: BookingDay[];
}

/**
 * إطار المعاينة — يُفتح داخل `iframe` من محرّر الصفحة.
 *
 * لماذا إطارٌ لا مكوّنٌ في الصفحة نفسها؟ لأن العميل يفتح الصفحة في
 * هاتفٍ عرضُه ٣٧٥ نقطة، وقواعدُ التنسيق تسأل عن عرض النافذة لا عرض
 * الصندوق: مكوّنٌ في صندوقٍ ضيّق داخل نافذةٍ واسعة يرى نافذةً واسعة.
 * والإطارُ نافذةٌ بذاتها، فتصدق فيه المقاسات كما تصدق عند العميل.
 *
 * البيانات تصل رسالةً من النافذة الأمّ — من الأصل نفسه، ولا تُقبل من
 * غيره — وتُعاد مع كل تغييرٍ في المحرّر فتتغيّر الصفحة تحت عينه.
 */
export function BookingPreview() {
  const [state, setState] = useState<{
    info: PublicBookingPage;
    days: BookingDay[];
  } | null>(null);

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      const d = e.data as Partial<BookingPreviewMessage> | undefined;
      if (d?.type === "goyone.bookingPreview" && d.info) {
        setState({ info: d.info, days: d.days ?? [] });
      }
    };
    window.addEventListener("message", onMessage);
    /* يُعلن جاهزيّته فيبعث المحرّر بياناته — لا انتظارَ لرسالةٍ سبقت التحميل. */
    window.parent?.postMessage(
      { type: "goyone.bookingPreviewReady" },
      window.location.origin,
    );
    return () => window.removeEventListener("message", onMessage);
  }, []);

  if (!state) {
    return (
      <Frame>
        <div className="skeleton" style={{ height: 220 }} />
      </Frame>
    );
  }

  return (
    <BookingScreen
      info={state.info}
      days={state.days}
      slotsPending={false}
      preview
      submit={() =>
        Promise.reject(new Error("هذه معاينة — لا يُحجز منها شيء."))
      }
    />
  );
}

/* ==========================================================================
   الشاشة
   ========================================================================== */

export function BookingScreen({
  info,
  days,
  slotsPending,
  submit,
  preview = false,
}: {
  info: PublicBookingPage;
  days: BookingDay[];
  slotsPending: boolean;
  submit: (body: BookingSubmission) => Promise<BookingResult>;
  /** معاينةٌ من المحرّر: الأوقات توضيحية ولا يُحجز شيء. */
  preview?: boolean;
}) {
  const [chosen, setChosen] = useState<{
    startAt: string;
    endAt: string;
  } | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  /*
   * الهاتفُ بمفتاح دولته.
   *
   * رقمٌ بلا مفتاحٍ لا يُتَّصل به ولا يُراسَل على واتساب من النظام.
   * فيُختار المفتاح — والافتراضُ مفتاحُ الشركة — ويُرسَل الرقمُ موحَّدًا
   * بعلامة الزائد كما يُحفظ في سائر النظام.
   */
  const [phoneCode, setPhoneCode] = useState(info.defaultCountryCode || "20");
  const phoneE164 = (() => {
    const raw = phone.trim();
    if (!raw) return "";
    const composed = raw.startsWith("+")
      ? raw
      : `+${phoneCode}${raw.replace(/\D/g, "").replace(/^0+/, "")}`;
    return normalizePhone(composed).value?.e164 ?? composed;
  })();
  const [note, setNote] = useState("");
  /* إجاباتُ ما زاد على الأربعة الثابتة — بمفاتيح حقول الصفحة. */
  const [answers, setAnswers] = useState<Record<string, BookingAnswer>>({});
  /* ما يختاره الحاجز قبل وقته: غرضُ الحجز ووسيلتُه. */
  const [purpose, setPurpose] = useState("");
  const [mode, setMode] = useState("");
  /* المدّة المختارة بالدقائق — للمدى فقط، ووحدتُها مدّةُ الصفحة. */
  const [durationMin, setDurationMin] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /* الموافقة على الشروط — تُعلَن بمربّعٍ لا تُفترض. */
  const [agreed, setAgreed] = useState(false);
  useReveal(info.layout === "SHOW", info.show?.chapters?.length ?? 0);
  const [done, setDone] = useState<BookingResult | null>(null);

  const book = useMutation({
    mutationFn: () =>
      submit({
        startAt: chosen?.startAt ?? "",
        name: name.trim(),
        email: email.trim(),
        phone: phoneE164 || undefined,
        note: note.trim() || undefined,
        purpose: purpose || undefined,
        mode: mode || undefined,
        durationMin:
          info.durationMode === "RANGE" && durationMin
            ? durationMin
            : undefined,
        answers: Object.keys(answers).length ? answers : undefined,
        agreedTerms: agreed,
      }),
    onSuccess: (res) => setDone(res),
    onError: (e) => setError(e instanceof Error ? e.message : "تعذّر الحجز."),
  });

  /* مدى أسعار الأغراض — يُعلَن في الصدر قبل الاختيار. */
  const priceRange = (() => {
    const prices = (info.purposes ?? [])
      .map((x) => x.price)
      .filter((x): x is number => x != null);
    if (!prices.length) {
      return info.price != null
        ? `${info.price.toLocaleString("en-US")} ج.م`
        : null;
    }
    const lo = Math.min(...prices);
    const hi = Math.max(...prices);
    return lo === hi
      ? `${lo.toLocaleString("en-US")} ج.م`
      : `من ${lo.toLocaleString("en-US")} إلى ${hi.toLocaleString("en-US")} ج.م`;
  })();

  /*
   * السعر المعروض يتبع الغرض المختار.
   *
   * أغراضُ الصفحة قد تختلف أسعارها، فعرضُ سعرٍ واحدٍ عليها جميعًا
   * يُخبر العميل برقمٍ لن يُحاسَب به. وما لا سعر له يأخذ سعر الصفحة.
   */
  const activePrice =
    info.purposes?.find((x) => x.name === purpose)?.price ?? info.price ?? null;

  /* المدّة الافتراضية وحدةٌ واحدة — أقلُّ ما يُحجز. */
  useEffect(() => {
    if (!durationMin) setDurationMin(info.durationMin);
  }, [info.durationMin, durationMin]);

  /* الوسيلة الافتراضية أوّلُ المتاح — فلا يُترك الاختيار فارغًا. */
  useEffect(() => {
    const first = info.modes?.[0];
    if (first && (!mode || !info.modes.includes(mode))) setMode(first);
  }, [info.modes, mode]);

  /*
   * النموذج كما بناه صاحبُ الصفحة — حقلًا حقلًا بترتيبه وإلزامه.
   *
   * الأربعةُ الثابتة لها حالاتُها، وما زاد يُكتب في `answers` بمفتاحه.
   * والزرُّ لا يُفتح حتى تمتلئ اللازمة كلُّها: الخادمُ يرفضها باسمها،
   * لكن الرفضَ بعد النقر أسوأ من زرٍّ ينتظر.
   */
  const fields = info.guestFields?.length
    ? info.guestFields
    : [
        { key: "name", label: "الاسم", required: true },
        { key: "email", label: "البريد الإلكتروني", required: true },
      ];
  const valueOf = (key: string): string => {
    if (key === "name") return name;
    if (key === "email") return email;
    if (key === "phone") return phone;
    if (key === "note") return note;
    const v = answers[key];
    return typeof v === "string" ? v : "";
  };
  const fileOf = (key: string) => {
    const v = answers[key];
    return typeof v === "object" && v ? v : null;
  };
  const setValue = (key: string, v: string) => {
    if (key === "name") setName(v);
    else if (key === "email") setEmail(v);
    else if (key === "phone") setPhone(v);
    else if (key === "note") setNote(v);
    else setAnswers((prev) => ({ ...prev, [key]: v }));
  };
  const setFile = (key: string, f: { name: string; url: string } | null) =>
    setAnswers((prev) => {
      const next = { ...prev };
      if (f) next[key] = f;
      else delete next[key];
      return next;
    });
  const missingRequired = fields.some(
    (f) => f.required && !valueOf(f.key).trim() && !fileOf(f.key),
  );

  /* لون الشركة يصبغ الأزرار والاختيار — الصفحة صفحتها لا صفحتنا. */
  const brand = info.brandColor
    ? ({
        ["--book-brand" as string]: info.brandColor,
        ["--book-brand-ink" as string]: readableOn(info.brandColor),
      } as React.CSSProperties)
    : undefined;

  /* ---------- تمّ ---------- */
  if (done) {
    return (
      <Frame brandStyle={brand}>
        <Identity info={info} />
        <div className="book-done">
          <span className="book-check" aria-hidden="true">
            <Icon name="check" size={26} />
          </span>
          <h1 className="book-title">تمّ الحجز</h1>
          <p className="book-when">
            {longDay(done.startAt)} — {time(done.startAt)}
          </p>
          <p className="mute">
            {done.hostName ? `مع ${done.hostName} — ` : ""}
            {done.tenantName}
          </p>
          {/*
            لا وعد ببريد لم يُرسَل — و«أضِفه إلى تقويمك» ملف ICS يُبنى في
            المتصفح ويفهمه كل تقويم: Google وOutlook وهاتف الزائر.
          */}
          <a
            className="btn book-cta mt-4"
            href={icsHref(info.name, done)}
            download="booking.ics"
          >
            أضِفه إلى تقويمك
          </a>
          <p className="tiny mute mt-4">
            سُجّل الموعد. احتفظ بهذه الصفحة أو دوّن الوقت — وإن احتجت تعديله
            فتواصل مع {info.tenantName}.
          </p>
        </div>
      </Frame>
    );
  }

  /* الشروط بأنواعها الثلاثة — إن وُجد واحدٌ وجب أن يُقرأ ويُوافَق عليه. */
  const hasTerms = Boolean(
    info.priceNote ||
    info.cancellationPolicy ||
    (info.extraTerms?.length ?? 0) > 0,
  );
  const cover = info.media.find((m) => m.kind === "IMAGE");
  const onsiteOnly = info.modes.length === 1 && info.modes[0] === "ONSITE";
  const unit =
    info.durationMode === "RANGE"
      ? ` / ${durationLabel(info.priceUnitMin)}`
      : "";
  const scrollTo = (id: string) => {
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  /*
   * الأجزاء التي يتشاركها الشكلان: الحجزُ نفسُه والشروطُ والذيل.
   * تُبنى مرّةً هنا وتوضع في العادية أو في العرض — فلا نسختان تفترقان.
   */
  const bookingSection = (
    <>
      {/* ---------------- الحجز ---------------- */}
      <section id="book" className="lp-section lp-book">
        <h2 className="lp-h2">احجز وقتك</h2>
        <p className="lp-sub mute">
          اختر يومًا ووقتًا من الشاغر، ثم أكمل بياناتك — لن يستغرق الأمر أكثر من
          دقيقة.
        </p>

        <div className="lp-book-card">
          {/* الأوقات */}
          <div className="lp-slots">
            {error && <div className="alert alert-danger mb-4">{error}</div>}
            {notice && <div className="alert alert-info mb-4">{notice}</div>}

            {slotsPending && (
              <div className="skeleton" style={{ height: 160 }} />
            )}

            {!slotsPending && days.length === 0 && (
              <p className="mute center">
                لا أوقات شاغرة في الأسبوعين القادمين. تواصل مع {info.tenantName}{" "}
                مباشرة.
              </p>
            )}

            {/*
              الأوقات تبقى ظاهرة بعد الاختيار: المختار يُعلَّم، ومن غيّر رأيه
              ينقر غيره — لا رجوع خطوةً إلى الوراء.
            */}
            {days.length > 0 && (
              <div className="book-days lp-days">
                {days.map((day) => (
                  <section key={day.date} className="book-day">
                    <h3 className="book-day-title">
                      {longDay(day.slots[0]?.startAt)}
                    </h3>
                    <div className="book-slots">
                      {day.slots.map((s) => (
                        <button
                          key={s.startAt}
                          className={`book-slot${chosen?.startAt === s.startAt ? " on" : ""}`}
                          aria-pressed={chosen?.startAt === s.startAt}
                          onClick={() => {
                            setChosen(s);
                            setError(null);
                          }}
                        >
                          {time(s.startAt)}
                        </button>
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            )}
          </div>

          {/* النموذج */}
          <div className="lp-form">
            {chosen ? (
              <>
                <div className="book-chosen">
                  <Icon name="check" size={16} />
                  <strong>{longDay(chosen.startAt)}</strong>
                  <span className="mute small">
                    {time(chosen.startAt)} — {time(chosen.endAt)}
                  </span>
                </div>

                <div className="book-fields lp-fields">
                  {/*
                    المدّة يختارها الحاجز حين تكون مدًى.

                    الاستوديو يُحجز ساعةً أو خمسًا، فالمدّة سؤالٌ لا ثابتٌ —
                    والقيمة تُحسب أمام عينه مع كل اختيار فلا يفاجئه رقمٌ بعد.
                  */}
                  {info.durationMode === "RANGE" && (
                    <div className="field">
                      <span className="label">إلى أيّ ساعة؟</span>
                      <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
                        {Array.from(
                          {
                            length: Math.max(
                              1,
                              Math.floor(
                                info.maxDurationMin / info.durationMin,
                              ),
                            ),
                          },
                          (_, i) => (i + 1) * info.durationMin,
                        ).map((m) => {
                          const end = new Date(
                            new Date(chosen.startAt).getTime() + m * 60_000,
                          );
                          return (
                            <button
                              key={m}
                              type="button"
                              className={`btn btn-sm${durationMin === m ? " btn-primary" : ""}`}
                              aria-pressed={durationMin === m}
                              onClick={() => setDurationMin(m)}
                            >
                              {timeFmt.format(end)}
                              <span className="tiny mute">
                                {` (${durationLabel(m)})`}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/*
                    الغرض مُنتقًى من قائمة الشركة لا منثورًا: يصل الموعد
                    معروفَ السبب فيُجهَّز له، ويُحصى بعدُ في التقارير.
                  */}
                  {info.purposes.length > 0 && (
                    <label className="field">
                      <span className="label">ما الغرض من الحجز؟</span>
                      <select
                        className="select"
                        value={purpose}
                        onChange={(e) => setPurpose(e.target.value)}
                      >
                        <option value="">اختر الغرض</option>
                        {info.purposes.map((x) => (
                          <option key={x.name} value={x.name}>
                            {x.name}
                            {x.price != null
                              ? ` — ${x.price.toLocaleString("en-US")} ج.م${unit}`
                              : ""}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}

                  {/*
                    قيمةُ الحجز بارزةً لا سطرًا صغيرًا.

                    كانت جملةً بخطٍّ دقيق تحت أزرار المدّة، فلا يراها من يقرّر
                    بها. صارت بطاقةً: القيمةُ رقمًا كبيرًا، والمقدَّمُ بنسبته،
                    والباقي في موعده — وتحتها كيف حُسبت. وتظهر للثابت والمدى معًا.
                  */}
                  {activePrice !== null &&
                    (() => {
                      const range = info.durationMode === "RANGE";
                      const total = range
                        ? Math.round(
                            (activePrice * durationMin) / info.priceUnitMin,
                          )
                        : Math.round(activePrice);
                      const deposit =
                        info.depositPercent > 0
                          ? Math.round((total * info.depositPercent) / 100)
                          : 0;
                      return (
                        <div className="lp-total" aria-live="polite">
                          <div className="lp-total-row is-total">
                            <span>قيمة الحجز</span>
                            <strong className="num">
                              {total.toLocaleString("en-US")} ج.م
                            </strong>
                          </div>
                          {deposit > 0 && (
                            <>
                              <div className="lp-total-row is-deposit">
                                <span>
                                  المقدَّم المطلوب ({info.depositPercent}%)
                                </span>
                                <strong className="num">
                                  {deposit.toLocaleString("en-US")} ج.م
                                </strong>
                              </div>
                              <div className="lp-total-row is-rest">
                                <span>الباقي في موعدك</span>
                                <span className="num">
                                  {(total - deposit).toLocaleString("en-US")}{" "}
                                  ج.م
                                </span>
                              </div>
                            </>
                          )}
                          <span className="tiny lp-total-note">
                            {range
                              ? `${durationLabel(durationMin)} × ${activePrice.toLocaleString("en-US")} ج.م لكل ${durationLabel(info.priceUnitMin)}`
                              : purpose
                                ? `سعر «${purpose}»`
                                : "سعر الجلسة"}
                          </span>
                        </div>
                      );
                    })()}

                  {/* الوسيلة: أونلاين برابطٍ يصلك، أو حضوريًّا في مكانٍ معلوم. */}
                  {info.modes.length > 1 && (
                    <div className="field">
                      <span className="label">كيف تفضّل الحجز؟</span>
                      <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
                        {info.modes.map((m) => (
                          <button
                            key={m}
                            type="button"
                            className={`btn btn-sm${mode === m ? " btn-primary" : ""}`}
                            aria-pressed={mode === m}
                            onClick={() => setMode(m)}
                          >
                            <Icon
                              name={m === "ONSITE" ? "mapPin" : "video"}
                              size={13}
                            />
                            {m === "ONSITE" ? "حضوريًّا" : "أونلاين"}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* الحضوريّ يقول أين — عنوانًا يُقرأ وموقعًا يُفتح. */}
                  {(mode === "ONSITE" || onsiteOnly) && info.locationText && (
                    <span className="tiny mute">
                      <Icon name="mapPin" size={12} /> {info.locationText}
                      {info.locationMapsUrl && (
                        <>
                          {" — "}
                          <a
                            href={info.locationMapsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            الموقع على الخريطة
                          </a>
                        </>
                      )}
                    </span>
                  )}

                  {/* حقول النموذج كما بُنيت — بترتيبها وإلزامها، وكلُّ نوعٍ بحقله. */}
                  {fields.map((f) => {
                    const cat = GUEST_FIELD_CATALOG.find(
                      (c) => c.key === f.key,
                    );
                    /* السؤال الحرّ بنوعه المختار؛ وعناصرُ الفهرس بنوعها. */
                    const input = f.input ?? cat?.input ?? "text";
                    const label = `${f.label}${f.required ? "" : " (اختياري)"}`;
                    const value = valueOf(f.key);
                    const opts = f.options ?? [];
                    const ltr =
                      input === "email" || input === "tel" || input === "url";
                    return (
                      <div className="field" key={f.key}>
                        <span className={`label${f.required ? " req" : ""}`}>
                          {label}
                        </span>

                        {input === "textarea" && (
                          <textarea
                            className="textarea"
                            rows={2}
                            value={value}
                            onChange={(e) => setValue(f.key, e.target.value)}
                            placeholder={
                              f.key === "note"
                                ? "اكتب ما تودّ مناقشته، ليجهّز من يقابلك."
                                : undefined
                            }
                          />
                        )}

                        {f.key === "phone" && (
                          <div className="lp-phone">
                            <CountrySelect
                              value={phoneCode}
                              onChange={setPhoneCode}
                              ariaLabel="مفتاح الدولة"
                            />
                            <span className="lp-phone-code num" dir="ltr">
                              +{phoneCode}
                            </span>
                            <input
                              className="input"
                              type="tel"
                              inputMode="tel"
                              value={value}
                              onChange={(e) => setValue(f.key, e.target.value)}
                              autoComplete="tel-national"
                              dir="ltr"
                              placeholder="1012345678"
                            />
                          </div>
                        )}

                        {f.key !== "phone" &&
                          (input === "text" ||
                            input === "email" ||
                            input === "tel" ||
                            input === "url" ||
                            input === "number" ||
                            input === "money" ||
                            input === "date" ||
                            input === "time" ||
                            input === "datetime") && (
                            <input
                              className={`input${input === "number" || input === "money" ? " num" : ""}`}
                              type={
                                input === "number" || input === "money"
                                  ? "number"
                                  : input === "datetime"
                                    ? "datetime-local"
                                    : input
                              }
                              inputMode={
                                input === "number" || input === "money"
                                  ? "decimal"
                                  : undefined
                              }
                              step={
                                input === "money"
                                  ? "any"
                                  : input === "number"
                                    ? "1"
                                    : undefined
                              }
                              value={value}
                              onChange={(e) => setValue(f.key, e.target.value)}
                              autoComplete={
                                f.key === "name"
                                  ? "name"
                                  : f.key === "email"
                                    ? "email"
                                    : f.key === "phone"
                                      ? "tel"
                                      : f.key === "company"
                                        ? "organization"
                                        : undefined
                              }
                              dir={ltr ? "ltr" : undefined}
                            />
                          )}
                        {input === "money" && (
                          <span className="tiny mute">
                            بالجنيه المصري (ج.م)
                          </span>
                        )}
                        {/*
                        العميلُ السابق يُعرف ببريده.

                        الحسابُ القائم يُربط به الحجز مباشرةً — صفقةً عليه لا
                        محتملًا ثانيًا — إن كتب البريدَ نفسَه المسجَّل عندنا.
                        فيُقال له ذلك تحت الحقل لا بعد أن يحجز ببريدٍ آخر.
                      */}
                        {f.key === "email" && (
                          <span className="tiny mute">
                            إن كنت عميلًا لدينا من قبل، فأدخل البريد نفسه
                            المسجَّل عندنا — ليُربط الحجز بحسابك مباشرة.
                          </span>
                        )}

                        {/* الاختيار الواحد أزرارٌ تُقرأ كلُّها — لا قائمةٌ تُفتح. */}
                        {input === "select" && (
                          <div
                            className="row"
                            style={{ gap: 6, flexWrap: "wrap" }}
                          >
                            {opts.map((o) => (
                              <button
                                key={o}
                                type="button"
                                className={`btn btn-sm${value === o ? " btn-primary" : ""}`}
                                aria-pressed={value === o}
                                onClick={() => setValue(f.key, o)}
                              >
                                {o}
                              </button>
                            ))}
                          </div>
                        )}

                        {input === "dropdown" && (
                          <select
                            className="select"
                            value={value}
                            onChange={(e) => setValue(f.key, e.target.value)}
                          >
                            <option value="">اختر</option>
                            {opts.map((o) => (
                              <option key={o} value={o}>
                                {o}
                              </option>
                            ))}
                          </select>
                        )}

                        {/* المتعدّد يُحفظ نصًّا مفصولًا بفواصل — يُقرأ كما كُتب. */}
                        {input === "multiselect" && (
                          <div
                            className="row"
                            style={{ gap: 6, flexWrap: "wrap" }}
                          >
                            {opts.map((o) => {
                              const picked = value ? value.split("، ") : [];
                              const on = picked.includes(o);
                              return (
                                <button
                                  key={o}
                                  type="button"
                                  className={`btn btn-sm${on ? " btn-primary" : ""}`}
                                  aria-pressed={on}
                                  onClick={() =>
                                    setValue(
                                      f.key,
                                      (on
                                        ? picked.filter((x) => x !== o)
                                        : [...picked, o]
                                      ).join("، "),
                                    )
                                  }
                                >
                                  {on && <Icon name="check" size={12} />}
                                  {o}
                                </button>
                              );
                            })}
                          </div>
                        )}

                        {input === "boolean" && (
                          <div className="row" style={{ gap: 6 }}>
                            {["نعم", "لا"].map((o) => (
                              <button
                                key={o}
                                type="button"
                                className={`btn btn-sm${value === o ? " btn-primary" : ""}`}
                                aria-pressed={value === o}
                                onClick={() => setValue(f.key, o)}
                              >
                                {o}
                              </button>
                            ))}
                          </div>
                        )}

                        {/* الملفُّ يُحمَل في الإجابة نفسها — حتى ميغابايت. */}
                        {input === "file" && (
                          <div
                            className="row"
                            style={{
                              gap: 8,
                              flexWrap: "wrap",
                              alignItems: "center",
                            }}
                          >
                            <input
                              className="input"
                              type="file"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                e.target.value = "";
                                if (!file) return;
                                if (file.size > MAX_BOOKING_FILE_BYTES) {
                                  setError(
                                    "الملف أكبر من ميغابايت — اختر ملفًّا أصغر.",
                                  );
                                  return;
                                }
                                const reader = new FileReader();
                                reader.onload = () =>
                                  setFile(f.key, {
                                    name: file.name,
                                    url: String(reader.result ?? ""),
                                  });
                                reader.readAsDataURL(file);
                              }}
                            />
                            {fileOf(f.key) && (
                              <span className="tiny">
                                <Icon name="paperclip" size={12} />{" "}
                                {fileOf(f.key)?.name}{" "}
                                <button
                                  type="button"
                                  className="btn btn-sm btn-ghost"
                                  onClick={() => setFile(f.key, null)}
                                >
                                  إزالة
                                </button>
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/*
                  الموافقة على الشروط تسبق الحجز.

                  الشروطُ تُقرأ في قسمها أدناه، والموافقةُ تُعلَن هنا بمربّعٍ
                  لا يُفترض: زرُّ الحجز لا يُفتح قبله، والخادمُ يرفض حجزًا
                  بلا موافقةٍ إن كانت للصفحة شروط — ويُسجَّل وقتُها.
                */}
                {hasTerms && (
                  <label className="lp-consent">
                    <input
                      type="checkbox"
                      checked={agreed}
                      onChange={(e) => setAgreed(e.target.checked)}
                    />
                    <span>
                      قرأتُ{" "}
                      <button
                        type="button"
                        className="lp-link"
                        onClick={() => scrollTo("terms")}
                      >
                        الشروط والأحكام
                        {info.cancellationPolicy
                          ? " وسياسة الإلغاء والاسترداد"
                          : ""}
                      </button>{" "}
                      وأوافق عليها.
                    </span>
                  </label>
                )}

                <button
                  className="btn book-cta lp-cta"
                  onClick={() => {
                    setError(null);
                    if (preview) {
                      setNotice(
                        "هذه معاينة — لا يُحجز منها شيء. انشر الصفحة وأرسل رابطها للعميل.",
                      );
                      return;
                    }
                    book.mutate();
                  }}
                  disabled={
                    book.isPending ||
                    missingRequired ||
                    name.trim().length < 2 ||
                    (hasTerms && !agreed)
                  }
                >
                  {book.isPending ? "جارٍ الحجز" : "أكّد الحجز"}
                </button>
              </>
            ) : (
              <div className="lp-form-empty">
                <Icon name="calendar" size={28} />
                <strong>اختر وقتًا من الشاغر</strong>
                <span className="tiny mute">
                  ثم أكمل بياناتك هنا — الموعد يُثبَّت فورًا.
                </span>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
  const termsSection = (
    <>
      {/* ---------------- الشروط ---------------- */}
      {hasTerms && (
        <section id="terms" className="lp-section lp-terms">
          <h2 className="lp-h2">الشروط والأحكام</h2>
          <p className="lp-sub mute">
            اقرأها قبل الحجز — وتُعاد إليك في رسالة التأكيد.
          </p>
          <div className="lp-terms-grid">
            {info.priceNote && (
              <article className="lp-term">
                <h3>
                  <Icon name="clipboard" size={15} /> الشروط والأحكام
                </h3>
                <p>{info.priceNote}</p>
              </article>
            )}
            {info.cancellationPolicy && (
              <article className="lp-term is-policy">
                <h3>
                  <Icon name="refresh" size={15} /> سياسة الإلغاء والاسترداد
                </h3>
                <p>{info.cancellationPolicy}</p>
              </article>
            )}
            {(info.extraTerms?.length ?? 0) > 0 && (
              <article className="lp-term">
                <h3>
                  <Icon name="wallet" size={15} /> شروط إضافية
                </h3>
                <ul className="book-extra">
                  {info.extraTerms.map((t, i) => (
                    <li key={`${t.title}-${i}`}>
                      <span>{t.title}</span>
                      {extraTermCost(t) && (
                        <span className="book-extra-cost num">
                          {extraTermCost(t)}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </article>
            )}
          </div>
        </section>
      )}
    </>
  );
  const footerSection = (
    <>
      {/* ---------------- الذيل ---------------- */}
      <footer className="lp-footer">
        <div className="lp-top-brand">
          {info.logoUrl ? (
            <img
              className="lp-top-logo"
              src={info.logoUrl}
              alt={info.tenantName}
            />
          ) : (
            <span className="lp-top-logo lp-top-logo-fallback">
              {info.tenantName.slice(0, 2)}
            </span>
          )}
          <span className="lp-top-name">{info.tenantName}</span>
        </div>
        <div className="lp-contact">
          {info.tenantPhone && (
            <a href={`tel:${info.tenantPhone}`} dir="ltr">
              <Icon name="phone" size={13} /> {info.tenantPhone}
            </a>
          )}
          {info.tenantEmail && (
            <a href={`mailto:${info.tenantEmail}`} dir="ltr">
              <Icon name="mail" size={13} /> {info.tenantEmail}
            </a>
          )}
          {info.tenantAddress && (
            <span>
              <Icon name="mapPin" size={13} /> {info.tenantAddress}
            </span>
          )}
        </div>
        <span className="tiny mute lp-powered">
          <Logo size={14} /> GoyOne
        </span>
      </footer>
    </>
  );

  /* ---------------- صفحة العرض (Show) ---------------- */
  if (info.layout === "SHOW") {
    const show = info.show;
    const chapters = show.chapters;
    return (
      <Frame brandStyle={brand} landing preview={preview}>
        <div className={`sh${show.theme === "dark" ? " is-dark" : ""}`}>
          {/* الشريط: فصولُ الصفحة تُقفز إليها — كفهرس الإصدار */}
          <header className="sh-top">
            <div className="lp-top-brand">
              {info.logoUrl ? (
                <img
                  className="lp-top-logo"
                  src={info.logoUrl}
                  alt={info.tenantName}
                />
              ) : (
                <span className="lp-top-logo lp-top-logo-fallback">
                  {info.tenantName.slice(0, 2)}
                </span>
              )}
              <span className="lp-top-name">{info.tenantName}</span>
            </div>
            <nav className="sh-nav" aria-label="فصول الصفحة">
              {chapters.map((c, i) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => scrollTo(`ch-${i}`)}
                >
                  {c.title}
                </button>
              ))}
              {info.purposes.length > 0 && (
                <button type="button" onClick={() => scrollTo("purposes")}>
                  الخدمات
                </button>
              )}
              {show.faq.length > 0 && (
                <button type="button" onClick={() => scrollTo("faq")}>
                  الأسئلة
                </button>
              )}
              {hasTerms && (
                <button type="button" onClick={() => scrollTo("terms")}>
                  الشروط
                </button>
              )}
            </nav>
            <button
              type="button"
              className="btn book-cta lp-top-cta"
              onClick={() => scrollTo("book")}
            >
              {show.ctaLabel}
            </button>
          </header>

          {/* البطل: عنوانٌ ضخم يُقرأ من بعيد */}
          <section
            className={`sh-hero${cover ? " has-cover" : ""}`}
            style={cover ? { backgroundImage: `url(${cover.url})` } : undefined}
          >
            <div className="sh-hero-inner">
              {show.kicker && <span className="sh-kicker">{show.kicker}</span>}
              <h1 className="sh-headline">{show.headline || info.name}</h1>
              {(show.subheadline || info.description) && (
                <p className="sh-sub">{show.subheadline || info.description}</p>
              )}
              <div className="sh-hero-actions">
                <button
                  type="button"
                  className="btn book-cta lp-cta"
                  onClick={() => scrollTo("book")}
                >
                  <Icon name="calendar" size={15} /> {show.ctaLabel}
                </button>
                {chapters.length > 0 && (
                  <button
                    type="button"
                    className="sh-ghost"
                    onClick={() => scrollTo("ch-0")}
                  >
                    اعرف أكثر <Icon name="chevronDown" size={14} />
                  </button>
                )}
              </div>
              <div className="lp-facts sh-facts">
                <span className="lp-fact">
                  <Icon name="clock" size={14} />
                  {info.durationMode === "RANGE"
                    ? `من ${durationLabel(info.durationMin)} إلى ${durationLabel(info.maxDurationMin)}`
                    : durationLabel(info.durationMin)}
                </span>
                {priceRange ? (
                  <span className="lp-fact is-strong">
                    <Icon name="wallet" size={14} />
                    {priceRange}
                    {unit}
                  </span>
                ) : (
                  <span className="lp-fact is-strong">
                    <Icon name="star" size={14} /> مجانًا
                  </span>
                )}
                {info.modes.map((m) => (
                  <span className="lp-fact" key={m}>
                    <Icon
                      name={m === "ONSITE" ? "mapPin" : "video"}
                      size={14}
                    />
                    {m === "ONSITE" ? "حضوريًّا" : "أونلاين"}
                  </span>
                ))}
              </div>
            </div>
          </section>

          {/* الأرقام البارزة */}
          {show.stats.length > 0 && (
            <section className="sh-stats sh-reveal" aria-label="أرقام">
              {show.stats.map((st, i) => (
                <div className="sh-stat" key={`${st.value}-${i}`}>
                  <strong className="num">{st.value}</strong>
                  <span>{st.label}</span>
                </div>
              ))}
            </section>
          )}

          {/* الفصول: نصٌّ وصورةٌ يتبادلان الجهة */}
          {chapters.map((c, i) => (
            <section
              id={`ch-${i}`}
              key={c.id}
              className={`sh-chapter sh-reveal${i % 2 === 1 ? " is-flip" : ""}`}
            >
              <div className="sh-chapter-text">
                <span className="sh-chapter-n num">
                  {String(i + 1).padStart(2, "0")}
                </span>
                {c.kicker && <span className="sh-kicker">{c.kicker}</span>}
                <h2 className="sh-chapter-title">{c.title}</h2>
                {c.body && <p className="sh-chapter-body">{c.body}</p>}
                {c.bullets.length > 0 && (
                  <ul className="sh-bullets">
                    {c.bullets.map((b, k) => (
                      <li key={`${b}-${k}`}>
                        <span className="sh-bullet-ic">
                          <Icon name="check" size={12} />
                        </span>
                        {b}
                      </li>
                    ))}
                  </ul>
                )}
                <button
                  type="button"
                  className="sh-ghost"
                  onClick={() => scrollTo("book")}
                >
                  {show.ctaLabel} <Icon name="chevronLeft" size={14} />
                </button>
              </div>
              <div className="sh-chapter-media">
                {c.media ? (
                  c.media.kind === "VIDEO" ? (
                    <iframe
                      src={embedUrl(c.media.url)}
                      title={c.media.caption ?? c.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture"
                      allowFullScreen
                      loading="lazy"
                    />
                  ) : (
                    <img
                      src={c.media.url}
                      alt={c.media.caption ?? c.title}
                      loading="lazy"
                    />
                  )
                ) : (
                  <div className="sh-media-empty">
                    <Icon name="layers" size={30} />
                  </div>
                )}
                {c.media?.caption && (
                  <span className="tiny sh-caption">{c.media.caption}</span>
                )}
              </div>
            </section>
          ))}

          {/* الخدمات — شريطٌ يُمرَّر أفقيًّا */}
          {info.purposes.length > 0 && (
            <section id="purposes" className="sh-section sh-reveal">
              <h2 className="lp-h2">ماذا تحجز؟</h2>
              <p className="lp-sub sh-muted">
                اختر ما يناسبك — ويمكنك تغييره عند الحجز.
              </p>
              <div className="sh-carousel">
                {info.purposes.map((x) => (
                  <button
                    type="button"
                    key={x.name}
                    className={`lp-purpose sh-card${purpose === x.name ? " is-on" : ""}`}
                    aria-pressed={purpose === x.name}
                    onClick={() => {
                      setPurpose(x.name);
                      scrollTo("book");
                    }}
                  >
                    <span className="lp-purpose-name">{x.name}</span>
                    <span className="lp-purpose-price num">
                      {x.price != null
                        ? `${x.price.toLocaleString("en-US")} ج.م${unit}`
                        : "بحسب الطلب"}
                    </span>
                    <span className="lp-purpose-go">
                      <Icon name="chevronLeft" size={14} />
                    </span>
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* المعرض */}
          {info.media.length > 0 && (
            <section id="gallery" className="sh-section sh-reveal">
              <h2 className="lp-h2">المعرض</h2>
              <div className="book-gallery lp-gallery">
                {info.media.map((m, i) => (
                  <figure
                    className="book-media"
                    key={`${m.url.slice(0, 48)}-${i}`}
                  >
                    {m.kind === "VIDEO" ? (
                      <iframe
                        src={embedUrl(m.url)}
                        title={m.caption ?? "فيديو"}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture"
                        allowFullScreen
                        loading="lazy"
                      />
                    ) : (
                      <img src={m.url} alt={m.caption ?? ""} loading="lazy" />
                    )}
                    {m.caption && (
                      <figcaption className="tiny sh-muted">
                        {m.caption}
                      </figcaption>
                    )}
                  </figure>
                ))}
              </div>
            </section>
          )}

          {bookingSection}

          {/* الأسئلة الشائعة */}
          {show.faq.length > 0 && (
            <section id="faq" className="sh-section sh-reveal">
              <h2 className="lp-h2">أسئلة شائعة</h2>
              <div className="sh-faq">
                {show.faq.map((f, i) => (
                  <details className="sh-faq-item" key={`${f.q}-${i}`}>
                    <summary>
                      <span>{f.q}</span>
                      <Icon name="chevronDown" size={14} />
                    </summary>
                    <p>{f.a}</p>
                  </details>
                ))}
              </div>
            </section>
          )}

          {termsSection}
          {footerSection}
        </div>
      </Frame>
    );
  }

  return (
    <Frame brandStyle={brand} landing preview={preview}>
      {/* ---------------- الشريط العلوي ---------------- */}
      <header className="lp-top">
        <div className="lp-top-brand">
          {info.logoUrl ? (
            <img
              className="lp-top-logo"
              src={info.logoUrl}
              alt={info.tenantName}
            />
          ) : (
            <span className="lp-top-logo lp-top-logo-fallback">
              {info.tenantName.slice(0, 2)}
            </span>
          )}
          <span className="lp-top-name">{info.tenantName}</span>
        </div>
        <nav className="lp-nav" aria-label="أقسام الصفحة">
          {info.media.length > 0 && (
            <button type="button" onClick={() => scrollTo("gallery")}>
              المعرض
            </button>
          )}
          {info.purposes.length > 0 && (
            <button type="button" onClick={() => scrollTo("purposes")}>
              الخدمات
            </button>
          )}
          {hasTerms && (
            <button type="button" onClick={() => scrollTo("terms")}>
              الشروط
            </button>
          )}
        </nav>
        <button
          type="button"
          className="btn book-cta lp-top-cta"
          onClick={() => scrollTo("book")}
        >
          احجز الآن
        </button>
      </header>

      {/* ---------------- البطل ---------------- */}
      <section
        className={`lp-hero${cover ? " has-cover" : ""}`}
        style={cover ? { backgroundImage: `url(${cover.url})` } : undefined}
      >
        <div className="lp-hero-inner">
          <span className="lp-kicker">{info.tenantName}</span>
          <h1 className="lp-title">{info.name}</h1>
          {info.description && <p className="lp-lead">{info.description}</p>}
          <div className="lp-facts">
            <span className="lp-fact">
              <Icon name="clock" size={14} />
              {info.durationMode === "RANGE"
                ? `من ${durationLabel(info.durationMin)} إلى ${durationLabel(info.maxDurationMin)}`
                : durationLabel(info.durationMin)}
            </span>
            {priceRange ? (
              <span className="lp-fact is-strong">
                <Icon name="wallet" size={14} />
                {priceRange}
                {unit}
              </span>
            ) : (
              <span className="lp-fact is-strong">
                <Icon name="star" size={14} /> مجانًا
              </span>
            )}
            {info.hostName && (
              <span className="lp-fact">
                <Icon name="user" size={14} /> {info.hostName}
              </span>
            )}
            {info.modes.map((m) => (
              <span className="lp-fact" key={m}>
                <Icon name={m === "ONSITE" ? "mapPin" : "video"} size={14} />
                {m === "ONSITE" ? "حضوريًّا" : "أونلاين"}
              </span>
            ))}
          </div>
          <div className="lp-hero-actions">
            <button
              type="button"
              className="btn book-cta lp-cta"
              onClick={() => scrollTo("book")}
            >
              <Icon name="calendar" size={15} /> اختر وقتك
            </button>
            {hasTerms && (
              <button
                type="button"
                className="lp-link"
                onClick={() => scrollTo("terms")}
              >
                اقرأ الشروط أولًا
              </button>
            )}
          </div>
        </div>
      </section>

      {/* ---------------- لماذا تحجز من هنا ---------------- */}
      <section className="lp-highlights" aria-label="مزايا الحجز">
        <div className="lp-highlight">
          <span className="lp-highlight-icon">
            <Icon name="zap" size={18} />
          </span>
          <strong>الموعد يُثبَّت فورًا</strong>
          <span className="tiny mute">
            تختار وقتًا شاغرًا فيُحجز لك في اللحظة نفسها.
          </span>
        </div>
        <div className="lp-highlight">
          <span className="lp-highlight-icon">
            <Icon name="mail" size={18} />
          </span>
          <strong>تأكيدٌ على بريدك</strong>
          <span className="tiny mute">
            تصلك التفاصيل كلُّها — الوقت والمكان وما يلزمك.
          </span>
        </div>
        <div className="lp-highlight">
          <span className="lp-highlight-icon">
            <Icon
              name={
                onsiteOnly || info.modes.includes("ONSITE") ? "mapPin" : "video"
              }
              size={18}
            />
          </span>
          <strong>
            {onsiteOnly
              ? "مكانٌ معلوم"
              : info.modes.includes("ONSITE")
                ? "حضوريًّا أو أونلاين"
                : "أونلاين بلا عناء"}
          </strong>
          <span className="tiny mute">
            {info.locationText && info.modes.includes("ONSITE")
              ? info.locationText
              : "رابط الاجتماع يصلك مع التأكيد."}
          </span>
        </div>
        <div className="lp-highlight">
          <span className="lp-highlight-icon">
            <Icon name="wallet" size={18} />
          </span>
          <strong>
            {!priceRange
              ? "مجانًا تمامًا"
              : info.depositPercent > 0
                ? `مقدَّم ${info.depositPercent}% فقط`
                : "بلا مقدَّم"}
          </strong>
          <span className="tiny mute">
            {!priceRange
              ? "لا رسوم ولا مقدَّم."
              : info.depositPercent > 0
                ? "والباقي في موعدك — التفاصيل في التأكيد."
                : "تُسدَّد القيمة في موعدك."}
          </span>
        </div>
      </section>

      {/* ---------------- المعرض ---------------- */}
      {info.media.length > 0 && (
        <section id="gallery" className="lp-section">
          <h2 className="lp-h2">المعرض</h2>
          <div className="book-gallery lp-gallery">
            {info.media.map((m, i) => (
              <figure className="book-media" key={`${m.url.slice(0, 48)}-${i}`}>
                {m.kind === "VIDEO" ? (
                  <iframe
                    src={embedUrl(m.url)}
                    title={m.caption ?? "فيديو"}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture"
                    allowFullScreen
                    loading="lazy"
                  />
                ) : (
                  <img src={m.url} alt={m.caption ?? ""} loading="lazy" />
                )}
                {m.caption && (
                  <figcaption className="tiny mute">{m.caption}</figcaption>
                )}
              </figure>
            ))}
          </div>
        </section>
      )}

      {/* ---------------- الخدمات ---------------- */}
      {info.purposes.length > 0 && (
        <section id="purposes" className="lp-section">
          <h2 className="lp-h2">ماذا تحجز؟</h2>
          <p className="lp-sub mute">
            اختر ما يناسبك — ويمكنك تغييره عند الحجز.
          </p>
          <div className="lp-purposes">
            {info.purposes.map((x) => (
              <button
                type="button"
                key={x.name}
                className={`lp-purpose${purpose === x.name ? " is-on" : ""}`}
                aria-pressed={purpose === x.name}
                onClick={() => {
                  setPurpose(x.name);
                  scrollTo("book");
                }}
              >
                <span className="lp-purpose-name">{x.name}</span>
                <span className="lp-purpose-price num">
                  {x.price != null
                    ? `${x.price.toLocaleString("en-US")} ج.م${unit}`
                    : "بحسب الطلب"}
                </span>
                <span className="lp-purpose-go">
                  <Icon name="chevronLeft" size={14} />
                </span>
              </button>
            ))}
          </div>
        </section>
      )}

      {bookingSection}

      {termsSection}

      {footerSection}
    </Frame>
  );
}

/** هوية الشركة: الشعار والاسمان — ما يطمئن الغريب أنه في المكان الصحيح. */
function Identity({ info }: { info: PublicBookingPage }) {
  return (
    <header className="book-brand">
      {info.logoUrl ? (
        <img className="book-logo" src={info.logoUrl} alt={info.tenantName} />
      ) : (
        <span className="book-logo book-logo-fallback">
          {info.tenantName.slice(0, 2)}
        </span>
      )}
      <div>
        <div className="book-tenant">{info.tenantName}</div>
        <h1 className="book-title">{info.name}</h1>
      </div>
    </header>
  );
}

/** إطار الصفحة — بلا شريط علوي ولا قائمة: الزائر ليس مستخدمًا في النظام. */
function Frame({
  children,
  brandStyle,
  wide,
  landing,
  preview,
}: {
  children: React.ReactNode;
  brandStyle?: React.CSSProperties;
  wide?: boolean;
  /** صفحةُ هبوطٍ كاملةُ العرض — لا بطاقةٌ في وسط الشاشة. */
  landing?: boolean;
  preview?: boolean;
}) {
  if (landing) {
    return (
      <div className="book-page lp-page" style={brandStyle}>
        {preview && (
          <div className="book-preview-bar is-landing">
            <Icon name="eye" size={13} /> معاينة — الأوقات توضيحية، ولا يُحجز
            منها شيء.
          </div>
        )}
        {children}
      </div>
    );
  }
  return (
    <div className="book-page" style={brandStyle}>
      {/* شريطُ المعاينة يقول ما هي: أوقاتٌ توضيحية ولا حجز — فلا تُظنّ صفحةً حيّة. */}
      {preview && (
        <div className="book-preview-bar">
          <Icon name="eye" size={13} /> معاينة — الأوقات توضيحية، ولا يُحجز منها
          شيء.
        </div>
      )}
      <div className={`book-card${wide ? " book-card-wide" : ""}`}>
        {children}
      </div>
      <p className="book-foot tiny mute">
        <Logo size={16} /> GoyOne
      </p>
    </div>
  );
}

/**
 * ملف تقويم iCalendar للموعد المحجوز.
 *
 * يُبنى نصًّا في المتصفح: صيغة ICS أسطرٌ مفتاح:قيمة يفهمها كل تقويم،
 * وجلبُ مكتبة لها كجلب رافعة لفتح ظرف.
 */
function icsHref(title: string, b: BookingResult): string {
  const stamp = (iso: string) =>
    new Date(iso)
      .toISOString()
      .replace(/[-:]/g, "")
      .replace(/\.\d{3}/, "");
  const esc = (v: string) => v.replace(/([,;\\])/g, "\\$1");

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//GoyOne//Booking//AR",
    "BEGIN:VEVENT",
    `UID:${stamp(b.startAt)}@goyone`,
    `DTSTAMP:${stamp(new Date().toISOString())}`,
    `DTSTART:${stamp(b.startAt)}`,
    `DTEND:${stamp(b.endAt)}`,
    `SUMMARY:${esc(title)}${b.hostName ? esc(` — مع ${b.hostName}`) : ""}`,
    `DESCRIPTION:${esc(`حجز لدى ${b.tenantName}`)}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ];

  return (
    "data:text/calendar;charset=utf-8," + encodeURIComponent(lines.join("\r\n"))
  );
}
